import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Leaf, CheckCircle, FilePdf, Eye, EyeSlash } from "@phosphor-icons/react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SignaturePad } from "../components/SignaturePad";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACK_TEXT = "I participated in the development and review of my care plan. The goals, schedule, services, task codes, assistance levels, preferences, safety information and backup arrangements were reviewed with me, and I had the opportunity to ask questions and request changes.";

export default function SharePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [openDoc, setOpenDoc] = useState(null);
  const [signing, setSigning] = useState(null);
  const [signName, setSignName] = useState({});
  const [signRel, setSignRel] = useState({});
  const [sig, setSig] = useState({});
  const [approvalType, setApprovalType] = useState("Client");
  const [approvalSig, setApprovalSig] = useState(null);
  const [approvalDone, setApprovalDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios.get(`${BASE}/share/${token}`)
      .then((r) => { setInfo(r.data); setApprovalDone(!!r.data.approval?.client_signed); })
      .catch((e) => setError(e?.response?.data?.detail || "This link is invalid or has expired."));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-md rounded-lg border border-stone-200 bg-white p-8 text-center shadow-sm" data-testid="share-error">
          <p className="font-heading text-lg font-semibold text-stone-900">Link unavailable</p>
          <p className="mt-2 text-sm text-stone-500">{error}</p>
        </div>
      </div>
    );
  }
  if (!info) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const signDoc = async (key) => {
    if (!(signName[key] || "").trim()) { alert("Please type the signer's full name"); return; }
    if (!sig[key]) { alert("Please draw a signature"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/share/${token}/sign/${key}`, {
        signer_name: signName[key].trim(), relationship: (signRel[key] || "").trim(), signature_png: sig[key], entries: [],
      });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Signing failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const signApproval = async () => {
    if (!approvalSig) { alert("Please draw a signature"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/share/${token}/sign-approval`, { signer_type: approvalType, signature_png: approvalSig });
      setApprovalDone(true);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Signing failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="share-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-6">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Leaf size={18} weight="fill" /></span>
          <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">Silver Home Care <span className="font-normal text-stone-500">— Document Signing</span></span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Documents to review & sign</h1>
        <p className="mt-1 text-sm text-stone-500">
          Care plan documents for <span className="font-semibold text-stone-800" data-testid="share-client-name">{info.client_name || "the client"}</span>
          {" "}<span className="text-stone-400">for <span className="font-semibold text-stone-600">{info.client_name || "the client"}</span></span> · This link expires {String(info.expires_at).slice(0, 10)}. Review each document, then sign with your finger or mouse.
        </p>

        <div className="mt-8 space-y-4">
          {info.documents.map((d) => (
            <div key={d.key} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`share-doc-${d.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-sm font-semibold text-stone-900">{d.title}</p>
                  <p className="mt-0.5 text-xs text-stone-500">{d.desc}</p>
                  {d.key === "service_agreement" && d.signed && !d.countersigned && (
                    <p className="mt-1 text-xs font-medium text-primary" data-testid="share-agency-prepared">Prepared &amp; signed by Silver Home Care — please review the rate and schedule, then countersign.</p>
                  )}
                </div>
                {d.signed && !(d.key === "service_agreement" && !d.countersigned) ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-3 py-1 text-xs font-medium text-[#1e8449]" data-testid={`share-signed-${d.key}`}>
                    <CheckCircle size={14} weight="fill" /> Signed by {d.signed.signer_name}{d.countersigned ? ` · Countersigned by ${d.countersigned.signer_name}` : ""}
                  </span>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setOpenDoc(openDoc === d.key ? null : d.key)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white"
                      data-testid={`share-view-${d.key}`}>
                      {openDoc === d.key ? <EyeSlash size={14} /> : <Eye size={14} />} {openDoc === d.key ? "Hide" : "Review"}
                    </button>
                    <button onClick={() => setSigning(signing === d.key ? null : d.key)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-stone-900"
                      data-testid={`share-sign-open-${d.key}`}>
                      <FilePdf size={14} /> Sign
                    </button>
                  </div>
                )}
              </div>
              {openDoc === d.key && (!d.signed || (d.key === "service_agreement" && !d.countersigned)) && (
                <iframe src={`${BASE}/share/${token}/template/${d.key}`} title={d.title}
                  className="mt-4 h-[420px] w-full rounded-lg border border-stone-200 bg-white" data-testid={`share-iframe-${d.key}`} />
              )}
              {signing === d.key && (!d.signed || (d.key === "service_agreement" && !d.countersigned)) && (
                <div className="mt-4 space-y-4 border-t border-stone-200 pt-4" data-testid={`share-sign-block-${d.key}`}>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Your full name *</Label>
                      <Input value={signName[d.key] || ""} onChange={(e) => setSignName({ ...signName, [d.key]: e.target.value })}
                        className="border-stone-300 bg-white" data-testid={`share-name-${d.key}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Relationship (if signing for the client)</Label>
                      <Input value={signRel[d.key] || ""} onChange={(e) => setSignRel({ ...signRel, [d.key]: e.target.value })}
                        className="border-stone-300 bg-white" placeholder="e.g. Daughter, POA" data-testid={`share-rel-${d.key}`} />
                    </div>
                  </div>
                  <SignaturePad onChange={(png) => setSig({ ...sig, [d.key]: png })} />
                  <div className="flex justify-end">
                    <Button onClick={() => signDoc(d.key)} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`share-sign-submit-${d.key}`}>
                      {busy ? "Signing..." : "Sign Document"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="share-approval">
          <h2 className="font-heading text-lg font-semibold text-stone-900">Care Plan Review & Approval {info.approval.version !== "v1" ? `(${info.approval.version})` : ""}</h2>
          <div className="mt-3 rounded-lg border border-stone-200 bg-accent p-4 text-sm leading-relaxed text-stone-700">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone-500">Acknowledgment</p>
            {ACK_TEXT}
          </div>
          {approvalDone ? (
            <p className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-3 py-1.5 text-sm font-medium text-[#1e8449]" data-testid="share-approval-signed">
              <CheckCircle size={16} weight="fill" /> Care plan signed — thank you.
            </p>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label>Signer type</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="share-approval-type">
                  {["Client", "POA", "Guardian", "Other authorized representative"].map((t) => (
                    <button key={t} type="button" onClick={() => setApprovalType(t)}
                      className={`rounded-md border px-3 py-1.5 text-sm transition-all ${approvalType === t ? "border-primary bg-primary/10 font-medium text-primary" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400 hover:text-stone-800"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <SignaturePad onChange={setApprovalSig} />
              <div className="flex justify-end">
                <Button onClick={signApproval} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="share-approval-submit">
                  {busy ? "Signing..." : "Sign Care Plan"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
